import {
    AkrossAdsSDK,
    init,
    getInstance,
    AACampaignResult,
    AACampaign,
    SDKConfig,
} from '@akross/artemis-ads-sdk';
import { environment, APP_VERSION } from '../config/environment';
import { StorageUtil } from '../utils/storage.util';
import {logger} from '../utils/logger.util';

// Instância global do SDK - acessível em qualquer lugar
let sdk: AkrossAdsSDK | null = null;

export class ArtemisService {
    constructor() {
        logger.debug("ArtemisService criado");
    }

    /**
     * Inicializa a SDK do Artemis Ads
     * Valores fixos configurados para o projeto Vivo Pontos
     * A instância é armazenada globalmente após inicialização
     */
    async initialize(): Promise<void> {
        const adserverUrl = environment.adserverBaseUrl;
        // SDK do Artemis requer URL absoluta. Em dev, adserverBaseUrl é um path relativo
        // que passa pelo proxy do Vite — converter para absoluto usando o origin atual.
        const baseUrl = adserverUrl.startsWith('http')
            ? adserverUrl
            : window.location.origin + adserverUrl;

        const sdkConfig: SDKConfig = {
            baseUrl: baseUrl,
            appVersion: APP_VERSION,
            debug: true,
            appId: '270',
            timeout: 10000,
            artemisAdsAppId: 'vivo-pontos-10ad-400c-88d9-fc32e2371e36',
            primaryColor: '#660099',
            proxyExternalScripts: true,
            cdnBaseUrl: 'https://content.vivofree.com.br'
        };

        sdk = await init(sdkConfig);
        logger.debug("SDK inicializada:", sdk?.isInitialized() ?? false);
    }

    /**
     * Define o userId na SDK
     * Deve ser chamado após login bem-sucedido
     * Assume que o SDK já foi inicializado no splash
     */
    async setUserId(userId: string): Promise<void> {
        if (!sdk) {
            logger.warnWithContext('ArtemisService', 'SDK não está disponível. Certifique-se de que o splash inicializou o SDK.');
            return;
        }

        try {
            await sdk.setUserId(userId);
            logger.logWithContext('ArtemisService', 'UserId configurado na SDK');
        } catch (error: unknown) {
            logger.error('[ArtemisService] Erro ao configurar userId:', error);
            throw error;
        }
    }

    /**
     * Configura custom headers na SDK
     * Injeta apenas X-AUTHORIZATION da mesma forma que o app usa
     * Assume que o SDK já foi inicializado no splash
     */
    async setCustomHeaders(token?: string | null): Promise<void> {
        if (!sdk) {
            logger.warnWithContext('ArtemisService', 'SDK não está disponível. Certifique-se de que o splash inicializou o SDK.');
            return;
        }

        const tokenToUse = token || StorageUtil.getToken();

        if (!tokenToUse) {
            logger.warnWithContext('ArtemisService', 'Token não encontrado, não é possível configurar X-AUTHORIZATION');
            return;
        }

        try {
            // Apenas X-AUTHORIZATION, da mesma forma que o app usa
            const customHeaders: Record<string, string> = {
                'X-AUTHORIZATION': tokenToUse,
                'X-CONSUMER': 'VIVOREWARDS'
            };

            await sdk.setCustomHeaders(customHeaders);
            logger.logWithContext('ArtemisService', 'Custom headers configurados na SDK');
        } catch (error: unknown) {
            logger.error('[ArtemisService] Erro ao configurar custom headers:', error);
            // Não lançar erro, apenas logar
        }
    }

    /**
     * Obtém instância global da SDK
     */
    getSDK(): AkrossAdsSDK | null {
        return getInstance();
    }

    /**
     * Busca campanhas de uma zona específica
     */
    async getCampaignsByZone(zoneId: string, size?: number): Promise<AACampaignResult> {
        try {
            if (!sdk) {
                throw new Error('SDK do Artemis não está inicializada');
            }

            // Atualizar headers caso o token tenha mudado
            const token = StorageUtil.getToken();
            if (token) {
                await this.setCustomHeaders(token);
            }

            // Buscar campanhas da zona
            const contextInfo: { [key: string]: string } = {
                host: window.location.host,
            };
            const response: AACampaignResult = await sdk.requestCampaign(zoneId, size, contextInfo, undefined);

            logger.debug('Artemis campaigns response recebida');

            return response;
        } catch (error: unknown) {
            logger.error('Erro ao buscar campanhas do Artemis:', error);
            const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
            throw new Error(`Erro ao buscar campanhas: ${errorMessage}`);
        }
    }

    /**
     * Envia evento de impressão de campanhas
     */
    async sendCampaignsImpression(trackingId: string, campaigns: AACampaign[]): Promise<void> {
        try {
            if (!sdk) {
                logger.warnWithContext('ArtemisService', 'SDK não disponível para enviar evento de impressão');
                return;
            }
            await sdk.sendCampaignsImpressionEvent(trackingId, campaigns);
        } catch (error: unknown) {
            logger.error('Erro ao enviar evento de impressão:', error);
            // Não lançar erro, apenas logar
        }
    }

    /**
     * Envia evento genérico via SDK do Artemis
     * @param eventName Nome do evento (ex: 'click', 'success_impression', 'textOpened')
     * @param campaignId ID da campanha
     * @param trackingId ID de tracking
     * @param mediaId ID da mídia (opcional)
     */
    async sendEvent(
        eventName: string,
        campaignId: string,
        trackingId: string,
        mediaId?: string
    ): Promise<void> {
        try {
            if (!sdk) {
                logger.warnWithContext('ArtemisService', `SDK não disponível para enviar evento ${eventName}`);
                return;
            }
            await sdk.sendEvent(eventName, campaignId, trackingId, mediaId);
            logger.debug(`[ArtemisService] Evento ${eventName} enviado via SDK`);
        } catch (error: unknown) {
            logger.error(`Erro ao enviar evento ${eventName}:`, error);
            // Não lançar erro, apenas logar
        }
    }

    /**
     * Limpa a SDK (usado no logout)
     */
    async logout(): Promise<void> {
        try {
            if (sdk) {
                await sdk.removeCustomHeaders();
                await sdk.setUserId('');
            }
            // Resetar todos os estados globais
            sdk = null;
            logger.logWithContext('ArtemisService', 'SDK limpa e estados resetados');
        } catch (error: unknown) {
            logger.error('[ArtemisService] Erro ao limpar SDK:', error);
            // Resetar estados mesmo em caso de erro
            sdk = null;
        }
    }
}

export const artemisService = new ArtemisService();

